<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcProjectSelector',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'optionSelected']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    fieldName: {
      type: String,
      required: false,
      default: 'project',
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const projects = ref(null);
  const messages = ref({
    searchPlaceholder: $fn.value.pgettext('Project', 'Search for invoice'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    projects: projects.value,
    messages: messages.value,
    ux: ux.value,
  }));

  const $$load_projects = async () => {
    return $fn.value.fetch('list_project');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'projects' }, { name: 'messages' }, { name: 'ux' }],
    loaders: { projects: $$load_projects },
    ux: {},
    dataRefs: { projects, messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.fieldName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fieldName');
    }
  );
  watch(
    () => props.clearable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('clearable');
    }
  );

  const act_7315092382398562_emit_kwz7j8bmw_getActionArgs = async ($event) => {
    return {
      event: 'optionSelected',
      value: $event.option,
    };
  };

  const act_7315092382398562_emit_kwz7j8bmw = new actionTypes.EmitAction({
    actionArgs: act_7315092382398562_emit_kwz7j8bmw_getActionArgs,
  });
  const inst_evh_7315092382398562_optionSelected_dskje1wrs =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_emit_kwz7j8bmw,
      event: 'optionSelected',
      displayName: 'emit',
    });
  const inst_evh_7315092382398562_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_optionSelected_dskje1wrs],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_optionSelected_dskje1wrs,
    inst_evh_7315092382398562_optionSelected,
  };

  const evh_7315092382398562_optionSelected_dskje1wrs = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_optionSelected_dskje1wrs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_optionSelected = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpFormSelect2
    :name="fieldName"
    v-model="item[fieldName]"
    :value="item[fieldName]"
    @input="
      !($event && typeof $event.stopPropagation === 'function') &&
      (item[fieldName] =
        $event && typeof $event === 'object' && 'data' in $event
          ? $event.data
          : $event)
    "
    bind-object
    :options="projects"
    valueField="id"
    textField="name"
    :clearable="clearable"
    :readonly="item.isReadonly"
    @optionSelected="evh_7315092382398562_optionSelected($event, {})"
  >
  </ItpFormSelect2>
</template>
